import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import LoadingOverlay from "../../../../../../../components/LoadingOverlay";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useResolveRequiringAction } from "../../../../../../../queries/patient-chart/requiring-action/use-resolve-requiring-action";
import { showToastOnApiCall } from "../../../../../../../utils/utils";
import AllScreeningAssessment from "./AllScreeningAssessment";
import FreeTextScreeningForm from "./FreeTextScreeningForm";
import { TitleMapper } from "./assessment-constant";
import { useScreeningAssessment } from "./hooks/use-screening-assessment";

interface AssessmentDrawerProps {
  disclosureHost: DisclosureHost;
  appointmentIdParam?: string;
  requiringActionId?: string;
}

export interface DataItem {
  answerOptions: string;
  question: string;
  questionId: string;
  type: string;
  uuid?: string;
}

export interface TableData {
  answerOptions: string[];
  screeners: DataItem[];
}

const AssessmentDrawer = ({
  disclosureHost,
  appointmentIdParam,
  requiringActionId,
}: AssessmentDrawerProps) => {
  const showSnackbar = useSnackbar();
  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { patientId } = useParams();
  const { mutateAsync: resolveRequiringAction } = useResolveRequiringAction();
  const queryClient = useQueryClient();

  const { questions, tableData, freeText, isLoading, setRadio, save } =
    useScreeningAssessment({
      type: disclosureHost?.data,
      appointmentId: appointmentid,
      prefillAppointmentId: appointmentIdParam ?? appointmentid,
      patientId,
      enabled: disclosureHost.opened,
    });

  const reviewHandler = () => {
    showToastOnApiCall({
      apiCall: resolveRequiringAction({
        requiringActionId: requiringActionId!,
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["requiring-action"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-requiring-action"],
        });
        disclosureHost.close();
      },
      showSnackbar,
    });
  };

  function handleClose() {
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Add Screening"
      dialogAction={
        <Flex gap={1}>
          {requiringActionId ? (
            <StyledButtonV2
              label="Mark As Reviewed"
              variant="contained"
              onClick={reviewHandler}
            />
          ) : (
            <StyledButtonV2
              label="Save"
              variant="contained"
              onClick={() => save(disclosureHost.close)}
            />
          )}
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "80vh",
        },
      }}
    >
      <Box pt={2}>
        <LoadingOverlay isLoading={isLoading} />
        {freeText.def ? (
          <FreeTextScreeningForm
            def={freeText.def}
            questions={questions}
            values={freeText.values}
            onChange={freeText.setValue}
            titleDetail={TitleMapper[disclosureHost.data]}
            disabled={!!requiringActionId}
          />
        ) : (
          <AllScreeningAssessment
            tableData={tableData as never}
            disabled={!!requiringActionId}
            handleRadioChange={setRadio}
            titleDetail={TitleMapper[disclosureHost.data]}
          />
        )}
      </Box>
    </StyledFormModal>
  );
};

export default AssessmentDrawer;
